import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Card, Tabs, Tag } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import {
  performFullSearchAsync,
  setQuery,
  selectSearchQuery,
  selectFullResults,
  selectLoadingFull,
} from "@/store/redux/slices/search/search_slice";
import { getSearchConfigForEntity } from "@/config/searchConfig";
import { getOrganizationId } from "@/utils/token_functions";

const SearchResults = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const organizationId = getOrganizationId();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlQuery = params.get("q") || "";

  const query = useSelector(selectSearchQuery);
  const fullResults = useSelector(selectFullResults);
  const loadingFull = useSelector(selectLoadingFull);

  useEffect(() => {
    if (!urlQuery) return;

    dispatch(setQuery(urlQuery));
    dispatch(performFullSearchAsync({ query: urlQuery, organizationId }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery]);

  const entities = fullResults?.entities || [];
  const totalResults = fullResults?.totalResults || 0;

  const handleResultClick = (entityKey, item) => {
    const config = getSearchConfigForEntity(entityKey);
    if (!config) return;

    const routeFromItem = config.routeFromItem?.(item);
    const id = item?.id || item?._id;
    const basePath = routeFromItem || (config.route ? config.route(id) : null);
    if (!basePath) return;

    const separator = basePath.includes("?") ? "&" : "?";
    navigate(`${basePath}${separator}highlight=true`);
  };

  const tabItems = entities
    .map((group) => {
      const config = getSearchConfigForEntity(group.entity);
      if (!config) return null;

      return {
        key: group.entity,
        label: `${config.label} (${group.total})`,
        children: (
          <div className="space-y-3">
            {group.results.map((item) => {
              const thumbnailUrl = config.getThumbnailUrl?.(item) || null;
              const avatarProps = config.getAvatarProps?.(item) || null;
              const badges = config.getBadges?.(item) || [];

              return (
                <Card
                  key={item.id || item._id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleResultClick(group.entity, item)}
                >
                  <div className="flex items-center gap-3">
                    {/* Left visual: media thumbnail, profile avatar, or entity initial */}
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={item.name || item.subject || config.label}
                        className="w-12 h-12 rounded-md object-cover flex-shrink-0"
                      />
                    ) : avatarProps ? (
                      <Avatar
                        src={avatarProps.src || null}
                        size="small"
                        style={{ flexShrink: 0 }}
                      >
                        {avatarProps.fallback || config.label?.[0] || "R"}
                      </Avatar>
                    ) : (
                      <Avatar className="flex-shrink-0 bg-primary text-white">
                        {config.label?.[0] || "R"}
                      </Avatar>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-small truncate">
                          {config.getPrimaryText?.(item) || item.name}
                        </h3>
                        <span className="text-xs text-gray-400 uppercase flex-shrink-0">
                          {config.label}
                        </span>
                      </div>

                      {config.getSecondaryText?.(item) && (
                        <p className="text-small text-gray-500 truncate mt-1">
                          {config.getSecondaryText(item)}
                        </p>
                      )}

                      {badges.length > 0 && (
                        <div className="mt-2 flex gap-1 flex-wrap">
                          {badges.map((badge) => (
                            <Tag
                              key={badge.key || `${group.entity}-${badge.label}`}
                              color={badge.color}
                            >
                              {badge.label}
                            </Tag>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ),
      };
    })
    .filter(Boolean);

  return (
    <div className="p-6">
      <h1 className="text-xl font-semibold mb-2">
        Search results for &quot;{query || urlQuery}&quot;
      </h1>
      <p className="text-small text-gray-500 mb-4">
        {loadingFull
          ? "Loading search results..."
          : `${totalResults} result${totalResults !== 1 ? "s" : ""} found`}
      </p>

      {!loadingFull && tabItems.length === 0 && (
        <div className="py-8 text-center text-gray-400 text-small">
          No results found for &quot;{query || urlQuery}&quot;
        </div>
      )}

      {tabItems.length > 0 && (
        <Tabs
          items={tabItems}
          defaultActiveKey={tabItems[0]?.key}
          destroyInactiveTabPane
        />
      )}
    </div>
  );
};

export default SearchResults;
